import React from 'react';
import {
  CompactTable,
  Loading,
  NetworkAsset,
  PageContent,
  useAssetConfig,
  useUserinfo,
  createUrl,
  assetSubTypeToPermission,
  useAssetQuery,
  useAssetTranslation,
  useAssetParams,
} from '@mss/mss-component-library';
import { Spin } from 'antd';
import { useCollectorsQuery } from '../../../asset/services/queries';
import columns from '../../../asset/components/collection/Columns';
import ActionMenu from '../../../asset/components/collection/ActionMenu';
import { patchAsset } from '../services/update';

export default function Collections() {
  const { t } = useAssetTranslation();
  const assetParams = useAssetParams();
  const { assetType, assetSubType, assetId } = assetParams;
  const { config } = useAssetConfig(assetSubType);
  const permissionSet = config.permissions.find(x =>
    x.includes(assetSubTypeToPermission(assetSubType))
  );

  const [hasPermission] = useUserinfo(assetId);
  const isNetworkAsset = assetType === 'network';
  const {
    data,
    refetch: refetchAsset,
    isLoading: isLoadingAsset,
  } = useAssetQuery(!isNetworkAsset);
  const { matchingConfig } = useAssetConfig(assetSubType);
  const [staticCollectorUpdating, setStaticCollectUpdating] =
    React.useState<boolean>(false);

  const asset = data as NetworkAsset;
  const query = useCollectorsQuery({
    presetUrl: createUrl(
      { assetType, assetSubType },
      `collectors${
        isNetworkAsset && asset ? `/?series_id=${asset.series}` : '/'
      }`,
      true
    ),
    enabledUrl: assetId
      ? `/api/core/asset/${assetId}/collectors/`
      : undefined,
    disabled: !asset,
  });

  const isLoading =
    query.collectors.isLoading ||
    query.isUpdating ||
    staticCollectorUpdating ||
    isLoadingAsset;
  const { staticCollectors, actions } = matchingConfig.form.collectors;

  return (
    <PageContent title={t('Collection Settings')} actions={actions ?? null}>
      {query.sets.isLoading && !asset && <Loading />}
      {!query.sets.isLoading &&
        !isLoadingAsset &&
        assetId &&
        asset &&
        query.sets.data.map((set, index) => (
          <Spin spinning={query.collectors.isLoading} key={set.key}>
            <h5 className={`mt-${index === 0 ? 1 : 12} mb-6`}>{set.label}</h5>
            <CompactTable
              rowKey="collector_key"
              data-ta={`collectors${set}Table`}
              pagination={false}
              columns={columns(
                collector => query.update({ collector, assetId }),
                ActionMenu,
                set.label,
                t,
                hasPermission(`change_${permissionSet}`),
                assetType === 'network'
                  ? hasPermission('change_networkconfig')
                  : undefined,
                assetType === 'network'
                  ? hasPermission('view_networkconfig')
                  : undefined,
                query.raw
              )}
              loading={isLoading}
              dataSource={
                query.collectors.data[set.key]?.filter(x =>
                  x.collector_key === 'network_config'
                    ? hasPermission('view_networkconfig')
                    : true
                ) || []
              }
            />
          </Spin>
        ))}
      {staticCollectors &&
        asset &&
        Object.keys(staticCollectors).map(setKey => {
          const set = staticCollectors[setKey];
          return (
            <Spin spinning={query.collectors.isLoading} key={setKey}>
              <h5 className="mt-12 mb-6">{set.label}</h5>
              <CompactTable
                rowKey="key"
                data-ta={`collectors${setKey}Table`}
                pagination={false}
                columns={columns(
                  async newCollector => {
                    setStaticCollectUpdating(true);
                    try {
                      await patchAsset(assetParams, {
                        ...asset,
                        [setKey]: newCollector.enabled
                          ? // @ts-ignore
                            [...asset[setKey], newCollector.key]
                          : // @ts-ignore
                            asset[setKey].filter(x => x !== newCollector.key),
                      });
                      refetchAsset();
                    } catch {
                      console.error('fail');
                    } finally {
                      setStaticCollectUpdating(false);
                    }
                  },
                  ActionMenu,
                  staticCollectors[setKey].label,
                  t,
                  hasPermission(`change_${permissionSet}`),
                  undefined,
                  undefined,
                  undefined,
                  true
                )}
                loading={isLoading}
                dataSource={set.collectors.map(collector => ({
                  ...collector,
                  // @ts-ignore
                  enabled: asset[setKey].includes(collector.key),
                }))}
              />
              {staticCollectors[setKey].extraInfo && (
                <>{staticCollectors[setKey].extraInfo}</>
              )}
            </Spin>
          );
        })}
    </PageContent>
  );
}
